import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, addEdge
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { nodeTypes, getNodePalette } from "@/components/WorkflowNodes";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Save, X, Play, Pause, Trash2 } from "lucide-react";

const defaultNodes = [
  { id: "start-1", type: "start", position: { x: 250, y: 50 }, data: { label: "Start" } }
];

export default function WorkflowBuilder() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEdit = !!id;
  const reactFlowWrapper = useRef(null);
  const { user } = useAuth();

  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [agentId, setAgentId] = useState("");
  const [status, setStatus] = useState("draft");
  const [agents, setAgents] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [triggers, setTriggers] = useState({ match: "any", priority: 10, rules: [] });
  const [sessions, setSessions] = useState([]);

  // Load the business's calendars when a Booking node is selected
  useEffect(() => {
    if (selectedNode?.type !== "booking") return;
    const bid = user?.business_id;
    api.get("/ghl/calendars", { params: bid ? { business_id: bid } : {} })
      .then(r => setCalendars(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCalendars([]));
  }, [selectedNode?.id, selectedNode?.type, user?.business_id]);

  // Load the sub-account's GHL custom fields when an Objective (gather) node is selected,
  // so the CRM Field dropdown maps to real fields instead of typing them by hand
  useEffect(() => {
    if (selectedNode?.type !== "objective") return;
    const bid = user?.business_id;
    api.get("/ghl/custom-fields", { params: bid ? { business_id: bid } : {} })
      .then(r => setCustomFields(r.data?.fields || []))
      .catch(() => setCustomFields([]));
  }, [selectedNode?.id, selectedNode?.type, user?.business_id]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: ag } = await api.get("/agents");
        setAgents(ag);
        // Sessions for the "channel" trigger rule (which number received the message)
        api.get("/whatsapp/sessions").then(r => setSessions(r.data?.sessions || [])).catch(() => null);
        if (isEdit) {
          const { data: wf } = await api.get(`/workflows/${id}`);
          setName(wf.name || "");
          setDescription(wf.description || "");
          setAgentId(wf.agent_id || "");
          setStatus(wf.status || "draft");
          if (wf.nodes && wf.nodes.length > 0) setNodes(wf.nodes);
          if (wf.edges) setEdges(wf.edges);
          if (wf.triggers) setTriggers({ match: "any", priority: 10, rules: [], ...wf.triggers });
        }
      } catch (e) {
        toast.error(t("builder.toastLoadFailed"));
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const onConnect = useCallback((params) => {
    setEdges((eds) => {
      // Replace any existing edge from the same source handle (backend follows only the first match)
      const srcHandle = params.sourceHandle || null;
      const filtered = eds.filter(e => !(e.source === params.source && (e.sourceHandle || null) === srcHandle));
      return addEdge({ ...params, animated: true, style: { stroke: "#27272A" } }, filtered);
    });
  }, [setEdges]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("application/reactflow");
    if (!type || !reactFlowInstance) return;
    if (type === "start") return; // Start node is unique and always present
    const position = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position,
      data: {
        label: getNodePalette().find(n => n.type === type)?.label || type,
        config: getDefaultConfig(type),
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [reactFlowInstance, setNodes]);

  const getDefaultConfig = (type) => {
    switch (type) {
      case "objective": return { objective_type: "gather", field_name: "", field_label: "", field_type: "text", ghl_custom_field: "", ghl_custom_field_custom: "", message_prompt: "", validation: "", prefill_behavior: "confirm_if_filled", confirm_prompt: "" };
      case "conversation": return { max_turns: 5 };
      case "conditional": return { field: "", operator: "equals", value: "" };
      case "booking": return { calendar_id: "", message: "" };
      case "webhook": return { url: "", method: "POST", headers: {}, body: "" };
      case "delay": return { duration: 5, unit: "minutes" };
      case "tag": return { action: "add", tag_name: "" };
      case "end": return { end_type: "complete", final_message: "", add_tag: "" };
      default: return {};
    }
  };

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  // Protect the Start node: filter out removal changes for it (keyboard delete, etc.)
  const handleNodesChange = useCallback((changes) => {
    const filtered = changes.filter(c => {
      if (c.type !== "remove") return true;
      const node = nodes.find(n => n.id === c.id);
      return node?.type !== "start";
    });
    if (filtered.length > 0) onNodesChange(filtered);
  }, [nodes, onNodesChange]);

  const deleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    toast.success(t("builder.toastNodeDeleted"));
  }, [setNodes, setEdges]);

  const onEdgeClick = useCallback((_, edge) => {
    setEdges(eds => eds.map(e => e.id === edge.id ? { ...e, selected: true } : { ...e, selected: false }));
    setSelectedNode(null);
  }, [setEdges]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setEdges(eds => eds.map(e => (e.selected ? { ...e, selected: false } : e)));
  }, [setEdges]);

  const updateNodeConfig = (key, value) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((n) => {
      if (n.id === selectedNode.id) {
        const config = { ...n.data.config, [key]: value };
        return { ...n, data: { ...n.data, config } };
      }
      return n;
    }));
    setSelectedNode(prev => ({
      ...prev,
      data: { ...prev.data, config: { ...prev.data.config, [key]: value } }
    }));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error(t("builder.nameRequired")); return; }
    // Objective nodes must be complete: Gather needs a field, Give Info needs a prompt
    const badGather = nodes.find(n => n.type === "objective" && (n.data?.config?.objective_type || "gather") === "gather" && !n.data?.config?.field_name);
    if (badGather) { toast.error(`Objective node "${badGather.data?.label || "Objective"}": select what data to collect`); return; }
    const badInform = nodes.find(n => n.type === "objective" && n.data?.config?.objective_type === "inform" && !n.data?.config?.message_prompt?.trim());
    if (badInform) { toast.error(`Give Info node "${badInform.data?.label || "Objective"}": write the Information Prompt`); return; }
    setSaving(true);
    const payload = { name, description, agent_id: agentId || null, nodes, edges, status, triggers };
    try {
      if (isEdit) {
        await api.put(`/workflows/${id}`, payload);
        toast.success(t("builder.toastSaved"));
      } else {
        const { data } = await api.post("/workflows", payload);
        toast.success(t("builder.toastCreated"));
        navigate(`/workflows/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(t("builder.toastSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!isEdit) {
      toast.error(t("builder.saveFirstActivate"));
      return;
    }
    
    const newStatus = status === "active" ? "draft" : "active";
    setActivating(true);
    
    try {
      await api.put(`/workflows/${id}`, { status: newStatus });
      setStatus(newStatus);
      toast.success(newStatus === "active" ? "Workflow activated! It will now be used by the AI agent." : "Workflow deactivated. It's now in draft mode.");
    } catch (e) {
      toast.error(t("builder.toastStatusFailed"));
    } finally {
      setActivating(false);
    }
  };

  const renderNodeConfig = () => {
    if (!selectedNode) return null;
    const config = selectedNode.data?.config || {};
    const type = selectedNode.type;

    return (
      <Card className="bg-white border-gray-200 animate-slide-in" data-testid="node-config-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-['Manrope']">{selectedNode.data?.label || type}</CardTitle>
            <button onClick={() => setSelectedNode(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {type === "objective" && (
            <>
              {/* Objective Type Selector */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">{t("builder.objectiveType")}</Label>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    data-testid="objective-type-inform"
                    onClick={() => updateNodeConfig("objective_type", "inform")}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 border ${
                      config.objective_type === "inform"
                        ? "bg-[#0A84FF]/15 border-[#0A84FF]/50 text-[#0A84FF]"
                        : "bg-secondary/30 border-transparent text-muted-foreground hover:bg-secondary/50"
                    }`}
                  >
                    {t("builder.giveInfo")}
                  </button>
                  <button
                    data-testid="objective-type-gather"
                    onClick={() => updateNodeConfig("objective_type", "gather")}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 border ${
                      config.objective_type === "gather"
                        ? "bg-[#FFD60A]/15 border-[#FFD60A]/50 text-[#FFD60A]"
                        : "bg-secondary/30 border-transparent text-muted-foreground hover:bg-secondary/50"
                    }`}
                  >
                    {t("builder.gatherInfo")}
                  </button>
                </div>
              </div>

              {config.objective_type === "gather" ? (
                <>
                  {/* Data to Collect */}
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("builder.dataToCollect")}</Label>
                    <Select
                      value={config.field_name || ""}
                      onValueChange={(v) => {
                        const labels = { full_name: t("builder.fieldFullName"), first_name: t("builder.fieldFirstName"), last_name: t("builder.fieldLastName"), email: "Email", phone: t("builder.fieldPhone"), address: t("builder.fieldAddress"), city: t("builder.fieldCity"), state: t("builder.fieldState"), zip_code: t("builder.fieldZip"), company: t("builder.fieldCompany"), budget: t("builder.fieldBudget"), service_interest: t("builder.fieldServiceInterest"), preferred_date: t("builder.fieldPreferredDate"), custom: t("builder.customField") };
                        updateNodeConfig("field_name", v);
                        updateNodeConfig("field_label", labels[v] || v);
                        const ghlMap = { full_name: "contact.name", first_name: "contact.firstName", last_name: "contact.lastName", email: "contact.email", phone: "contact.phone", address: "contact.address1", city: "contact.city", state: "contact.state", zip_code: "contact.postalCode", company: "contact.companyName" };
                        if (ghlMap[v]) updateNodeConfig("ghl_custom_field", ghlMap[v]);
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent" data-testid="config-field-select">
                        <SelectValue placeholder={t("builder.selectField")} />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="full_name">{t("builder.fieldFullName")}</SelectItem>
                        <SelectItem value="first_name">{t("builder.fieldFirstName")}</SelectItem>
                        <SelectItem value="last_name">{t("builder.fieldLastName")}</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">{t("builder.fieldPhone")}</SelectItem>
                        <SelectItem value="address">{t("builder.fieldAddress")}</SelectItem>
                        <SelectItem value="city">{t("builder.fieldCity")}</SelectItem>
                        <SelectItem value="state">{t("builder.fieldState")}</SelectItem>
                        <SelectItem value="zip_code">{t("builder.fieldZip")}</SelectItem>
                        <SelectItem value="company">{t("builder.fieldCompany")}</SelectItem>
                        <SelectItem value="budget">{t("builder.fieldBudget")}</SelectItem>
                        <SelectItem value="service_interest">{t("builder.fieldServiceInterest")}</SelectItem>
                        <SelectItem value="preferred_date">{t("builder.fieldPreferredDate")}</SelectItem>
                        <SelectItem value="custom">{t("builder.customField")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {config.field_name === "custom" && (
                    <div>
                      <Label className="text-xs text-muted-foreground">{t("builder.customFieldName")}</Label>
                      <Input value={config.field_label || ""} onChange={(e) => updateNodeConfig("field_label", e.target.value)} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" placeholder={t("builder.customFieldPlaceholder")} data-testid="config-custom-field-name" />
                    </div>
                  )}

                  {/* CRM Field Mapping */}
                  <div>
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      CRM Field
                      <span className="text-primary/50 text-[10px]">(auto-mapped)</span>
                    </Label>
                    <Select
                      value={config.ghl_custom_field || ""}
                      onValueChange={(v) => {
                        if (v === "custom_ghl") {
                          updateNodeConfig("ghl_custom_field", config.ghl_custom_field_custom || "");
                        } else {
                          updateNodeConfig("ghl_custom_field", v);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent font-mono" data-testid="config-ghl-field-select">
                        <SelectValue placeholder={t("builder.mapCrmField")} />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="contact.name">contact.name</SelectItem>
                        <SelectItem value="contact.firstName">contact.firstName</SelectItem>
                        <SelectItem value="contact.lastName">contact.lastName</SelectItem>
                        <SelectItem value="contact.email">contact.email</SelectItem>
                        <SelectItem value="contact.phone">contact.phone</SelectItem>
                        <SelectItem value="contact.address1">contact.address1</SelectItem>
                        <SelectItem value="contact.city">contact.city</SelectItem>
                        <SelectItem value="contact.state">contact.state</SelectItem>
                        <SelectItem value="contact.postalCode">contact.postalCode</SelectItem>
                        <SelectItem value="contact.companyName">contact.companyName</SelectItem>
                        <SelectItem value="contact.dateOfBirth">contact.dateOfBirth</SelectItem>
                        <SelectItem value="contact.website">contact.website</SelectItem>
                        {customFields.length > 0 && (
                          <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-t border-border mt-1">
                            {t("builder.fromSubaccount", { count: customFields.length })}
                          </div>
                        )}
                        {customFields.map((f) => (
                          <SelectItem key={f.id} value={f.key}>
                            {f.name} <span className="text-muted-foreground/60 font-mono text-[10px]">({f.key})</span>
                          </SelectItem>
                        ))}
                        <SelectItem value="custom_ghl">{t("builder.customCrmField")}</SelectItem>
                      </SelectContent>
                    </Select>
                    {config.ghl_custom_field === "" && (
                      <Input
                        value={config.ghl_custom_field_custom || ""}
                        onChange={(e) => {
                          updateNodeConfig("ghl_custom_field_custom", e.target.value);
                          updateNodeConfig("ghl_custom_field", e.target.value);
                        }}
                        className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary font-mono mt-1.5"
                        placeholder="contact.custom_field_id"
                        data-testid="config-ghl-custom-input"
                      />
                    )}
                  </div>

                  {/* Bot Prompt */}
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("builder.botPrompt")}</Label>
                    <Textarea
                      value={config.message_prompt || ""}
                      onChange={(e) => updateNodeConfig("message_prompt", e.target.value)}
                      className="text-sm bg-secondary/50 border-transparent focus:border-primary resize-none"
                      rows={3}
                      placeholder="Write in natural language. Examples:&#10;- Ask the prospect for their full address&#10;- Pregunta al cliente su presupuesto aproximado&#10;- Request their preferred appointment date"
                      data-testid="config-gather-prompt"
                    />
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{t("builder.botPromptNote")}</p>
                  </div>

                  {/* Validation */}
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("builder.validation")}</Label>
                    <Select value={config.validation || "none"} onValueChange={(v) => updateNodeConfig("validation", v)}>
                      <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="none">{t("builder.noValidation")}</SelectItem>
                        <SelectItem value="email">{t("builder.validEmail")}</SelectItem>
                        <SelectItem value="phone">{t("builder.validPhone")}</SelectItem>
                        <SelectItem value="number">{t("builder.validNumber")}</SelectItem>
                        <SelectItem value="not_empty">{t("builder.notEmpty")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pre-fill Behavior - When the contact already has the data */}
                  <div className="pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground mb-2 block">{t("builder.prefillTitle")}</Label>
                    <div className="space-y-1.5">
                      {[
                        { value: "always_ask", label: t("builder.alwaysAsk"), desc: t("builder.alwaysAskDesc") },
                        { value: "confirm_if_filled", label: t("builder.confirmExisting"), desc: t("builder.confirmExistingDesc") },
                        { value: "skip_if_filled", label: t("builder.skipIfFilled"), desc: t("builder.skipIfFilledDesc") },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          data-testid={`prefill-${opt.value}`}
                          onClick={() => updateNodeConfig("prefill_behavior", opt.value)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors duration-200 border ${
                            config.prefill_behavior === opt.value
                              ? "bg-[#BF5AF2]/10 border-[#BF5AF2]/40 text-[#BF5AF2]"
                              : "bg-secondary/20 border-transparent text-muted-foreground hover:bg-secondary/40"
                          }`}
                        >
                          <span className="font-medium block">{opt.label}</span>
                          <span className="text-[10px] opacity-70">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom confirm prompt */}
                  {config.prefill_behavior === "confirm_if_filled" && (
                    <div>
                      <Label className="text-xs text-muted-foreground">{t("builder.confirmPrompt")}</Label>
                      <Textarea
                        value={config.confirm_prompt || ""}
                        onChange={(e) => updateNodeConfig("confirm_prompt", e.target.value)}
                        className="text-sm bg-secondary/50 border-transparent focus:border-primary resize-none"
                        rows={2}
                        placeholder="e.g., I see your email is {value}. Is that correct?&#10;Leave empty to use default confirmation"
                        data-testid="config-confirm-prompt"
                      />
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{t("builder.confirmPromptNote")}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Give Information - Prompt only */}
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("builder.informPrompt")}</Label>
                    <Textarea
                      value={config.message_prompt || ""}
                      onChange={(e) => updateNodeConfig("message_prompt", e.target.value)}
                      className="text-sm bg-secondary/50 border-transparent focus:border-primary resize-none"
                      rows={4}
                      placeholder="Describe what the bot should communicate. Examples:&#10;- Explain our pricing plans and current promotions&#10;- Tell the customer about our business hours and location&#10;- Presenta los servicios de la empresa"
                      data-testid="config-inform-prompt"
                    />
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{t("builder.informPromptNote")}</p>
                  </div>
                </>
              )}
            </>
          )}
          {type === "conversation" && (
            <div>
              <Label className="text-xs text-muted-foreground">{t("builder.maxTurns")}</Label>
              <Input type="number" value={config.max_turns || 5} onChange={(e) => updateNodeConfig("max_turns", parseInt(e.target.value))} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" />
            </div>
          )}
          {type === "conditional" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.fieldLabel")}</Label>
                <Input value={config.field || ""} onChange={(e) => updateNodeConfig("field", e.target.value)} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" placeholder="e.g., lead_score" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.operator")}</Label>
                <Select value={config.operator || "equals"} onValueChange={(v) => updateNodeConfig("operator", v)}>
                  <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="equals">{t("builder.equals")}</SelectItem>
                    <SelectItem value="not_equals">{t("builder.notEquals")}</SelectItem>
                    <SelectItem value="contains">{t("builder.contains")}</SelectItem>
                    <SelectItem value="greater_than">{t("builder.greaterThan")}</SelectItem>
                    <SelectItem value="less_than">{t("builder.lessThan")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.valueLabel")}</Label>
                <Input value={config.value || ""} onChange={(e) => updateNodeConfig("value", e.target.value)} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" />
              </div>
            </>
          )}
          {type === "booking" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.calendar")}</Label>
                {calendars.length > 0 && (
                  <Select
                    value={calendars.some(c => c.id === config.calendar_id) ? config.calendar_id : "__manual"}
                    onValueChange={(v) => {
                      if (v === "__manual") {
                        updateNodeConfig("calendar_id", "");
                        updateNodeConfig("calendar_name", "");
                      } else {
                        updateNodeConfig("calendar_id", v);
                        updateNodeConfig("calendar_name", calendars.find(c => c.id === v)?.name || "");
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent" data-testid="config-calendar-select">
                      <SelectValue placeholder={t("builder.selectCalendarPh")} />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {calendars.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="__manual">{t("builder.enterIdManually")}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {(calendars.length === 0 || !calendars.some(c => c.id === config.calendar_id)) && (
                  <Input
                    value={calendars.some(c => c.id === config.calendar_id) ? "" : (config.calendar_id || "")}
                    onChange={(e) => { updateNodeConfig("calendar_id", e.target.value); updateNodeConfig("calendar_name", ""); }}
                    className={`h-8 text-sm bg-secondary/50 border-transparent focus:border-primary font-mono ${calendars.length > 0 ? "mt-1.5" : ""}`}
                    placeholder={t("builder.calendarIdPh")}
                    data-testid="config-calendar-input"
                  />
                )}
                {calendars.length === 0 && (
                  <p className="text-[10px] text-muted-foreground/60 mt-1">
                    {t("builder.calendarLoadFailed")}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.bookingMessage")}</Label>
                <Textarea value={config.message || ""} onChange={(e) => updateNodeConfig("message", e.target.value)} className="text-sm bg-secondary/50 border-transparent focus:border-primary resize-none" rows={2} />
              </div>
            </>
          )}
          {type === "webhook" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">URL</Label>
                <Input value={config.url || ""} onChange={(e) => updateNodeConfig("url", e.target.value)} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary font-mono" placeholder="https://api.example.com/webhook" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.method")}</Label>
                <Select value={config.method || "POST"} onValueChange={(v) => updateNodeConfig("method", v)}>
                  <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {type === "delay" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.duration")}</Label>
                <Input type="number" value={config.duration || 5} onChange={(e) => updateNodeConfig("duration", parseInt(e.target.value))} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.unit")}</Label>
                <Select value={config.unit || "minutes"} onValueChange={(v) => updateNodeConfig("unit", v)}>
                  <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="seconds">{t("builder.seconds")}</SelectItem>
                    <SelectItem value="minutes">{t("builder.minutes")}</SelectItem>
                    <SelectItem value="hours">{t("builder.hours")}</SelectItem>
                    <SelectItem value="days">{t("builder.days")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {type === "tag" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.action")}</Label>
                <Select value={config.action || "add"} onValueChange={(v) => updateNodeConfig("action", v)}>
                  <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="add">{t("builder.addTag")}</SelectItem>
                    <SelectItem value="remove">{t("builder.removeTag")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.tagName")}</Label>
                <Input value={config.tag_name || ""} onChange={(e) => updateNodeConfig("tag_name", e.target.value)} className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" placeholder="e.g., ai_qualified" />
              </div>
            </>
          )}
          {type === "end" && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.endAction")}</Label>
                <Select value={config.end_type || "complete"} onValueChange={(v) => updateNodeConfig("end_type", v)}>
                  <SelectTrigger className="h-8 text-sm bg-secondary/50 border-transparent" data-testid="config-end-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="complete">{t("builder.endComplete")}</SelectItem>
                    <SelectItem value="handoff">{t("builder.endHandoff")}</SelectItem>
                    <SelectItem value="pause">{t("builder.endPause")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground/60 mt-1">
                  {config.end_type === "handoff"
                    ? t("builder.endHandoffNote")
                    : config.end_type === "pause"
                    ? t("builder.endPauseNote")
                    : t("builder.endCompleteNote")}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.finalMessage")}</Label>
                <Textarea 
                  value={config.final_message || ""} 
                  onChange={(e) => updateNodeConfig("final_message", e.target.value)} 
                  className="text-sm bg-secondary/50 border-transparent focus:border-primary resize-none" 
                  rows={3}
                  placeholder={t("builder.finalMessagePh")}
                  data-testid="config-end-message"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t("builder.endAddTag")}</Label>
                <Input 
                  value={config.add_tag || ""} 
                  onChange={(e) => updateNodeConfig("add_tag", e.target.value)} 
                  className="h-8 text-sm bg-secondary/50 border-transparent focus:border-primary" 
                  placeholder="e.g., workflow_completed"
                  data-testid="config-end-tag"
                />
                <p className="text-[10px] text-muted-foreground/60 mt-1">
                  {t("builder.endAddTagNote")}
                </p>
              </div>
            </>
          )}

          {/* Delete node */}
          <div className="pt-3 border-t border-border">
            <Button
              data-testid="delete-node-btn"
              variant="outline"
              size="sm"
              onClick={() => deleteNode(selectedNode.id)}
              className="w-full border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-400"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("builder.deleteNode")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="workflow-builder-page" className="h-screen flex flex-col">
      {/* Top bar */}
      <div className="h-14 border-b border-border flex items-center justify-between px-4 bg-gray-50 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/workflows")} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-foreground" data-testid="wf-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Input
            data-testid="workflow-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("builder.namePlaceholder")}
            className="bg-transparent border-transparent hover:border-border focus:border-primary h-8 w-60 text-sm font-semibold"
          />
        </div>
        <div className="flex items-center gap-3">
          {/* Status Badge */}
          {isEdit && (
            <Badge 
              variant="outline" 
              className={`text-xs capitalize ${status === "active" ? "border-green-500 text-green-500 bg-green-500/10" : "border-yellow-500 text-yellow-500 bg-yellow-500/10"}`}
              data-testid="workflow-status-badge"
            >
              {status === "active" ? t("builder.active") : t("builder.draft")}
            </Badge>
          )}
          <Select value={agentId} onValueChange={setAgentId}>
            <SelectTrigger className="h-8 w-48 text-sm bg-secondary/50 border-transparent" data-testid="wf-agent-select">
              <SelectValue placeholder={t("builder.assignAgent")} />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {agents.map(a => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Activate/Deactivate Button */}
          {isEdit && (
            <Button
              data-testid="toggle-workflow-status-btn"
              onClick={handleToggleStatus}
              disabled={activating}
              size="sm"
              variant={status === "active" ? "outline" : "default"}
              className={status === "active" 
                ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50" 
                : "bg-green-600 text-white hover:bg-green-700"}
            >
              {status === "active" ? (
                <>
                  <Pause className="w-3.5 h-3.5 mr-1.5" />
                  {activating ? "..." : t("builder.deactivate")}
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 mr-1.5" />
                  {activating ? "..." : t("builder.activate")}
                </>
              )}
            </Button>
          )}
          <Button
            data-testid="save-workflow-btn"
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-transform duration-150"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? t("builder.saving") : t("builder.save")}
          </Button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Node palette */}
        <div className="w-56 border-r border-border bg-gray-50 p-3 space-y-2 overflow-y-auto shrink-0" data-testid="node-palette">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("builder.nodesHeader")}</p>
          {getNodePalette().map((item) => (
            <div
              key={item.type}
              className="node-palette-item flex items-center gap-2.5 p-2.5 rounded-lg bg-secondary/50 hover:bg-secondary border border-transparent hover:border-border transition-colors duration-200"
              draggable
              onDragStart={(e) => { e.dataTransfer.setData("application/reactflow", item.type); e.dataTransfer.effectAllowed = "move"; }}
              data-testid={`palette-${item.type}`}
            >
              <div className="w-6 h-6 rounded flex items-center justify-center text-xs" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                {item.icon}
              </div>
              <span className="text-sm text-foreground">{item.label}</span>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground/50 pt-3 border-t border-border mt-3">
            {t("builder.paletteTip")}
          </p>

          {/* Trigger rules: when this workflow fires */}
          <div className="pt-3 border-t border-border mt-3 space-y-2" data-testid="triggers-panel">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("builder.triggers")}</p>
            <p className="text-[10px] text-muted-foreground/70">{t("builder.triggersNote")}</p>
            {triggers.rules.length > 0 && (
              <div className="space-y-2">
                {triggers.rules.map((rule, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-secondary/50 border border-border space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Select value={rule.type} onValueChange={(v) => {
                        const rules = [...triggers.rules];
                        rules[idx] = { type: v, op: v === "tag" ? "has" : (v === "keyword" ? "contains" : undefined), value: v === "new_contact" ? true : "" };
                        setTriggers({ ...triggers, rules });
                      }}>
                        <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tag">{t("builder.ruleTag")}</SelectItem>
                          <SelectItem value="keyword">{t("builder.ruleKeyword")}</SelectItem>
                          <SelectItem value="channel">{t("builder.ruleChannel")}</SelectItem>
                          <SelectItem value="new_contact">{t("builder.ruleNewContact")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => setTriggers({ ...triggers, rules: triggers.rules.filter((_, i) => i !== idx) })}
                        className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {rule.type === "tag" && (
                      <div className="flex gap-1.5">
                        <Select value={rule.op || "has"} onValueChange={(v) => {
                          const rules = [...triggers.rules]; rules[idx] = { ...rule, op: v }; setTriggers({ ...triggers, rules });
                        }}>
                          <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="has">{t("builder.has")}</SelectItem>
                            <SelectItem value="not_has">{t("builder.notHas")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={rule.value || ""} placeholder="fb-ads" className="h-7 text-xs"
                          onChange={(e) => { const rules = [...triggers.rules]; rules[idx] = { ...rule, value: e.target.value }; setTriggers({ ...triggers, rules }); }} />
                      </div>
                    )}
                    {rule.type === "keyword" && (
                      <div className="flex gap-1.5">
                        <Select value={rule.op || "contains"} onValueChange={(v) => {
                          const rules = [...triggers.rules]; rules[idx] = { ...rule, op: v }; setTriggers({ ...triggers, rules });
                        }}>
                          <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contains">{t("builder.contains")}</SelectItem>
                            <SelectItem value="equals">{t("builder.exact")}</SelectItem>
                            <SelectItem value="starts_with">{t("builder.startsWith")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={rule.value || ""} placeholder={t("builder.keywordPlaceholder")} className="h-7 text-xs"
                          onChange={(e) => { const rules = [...triggers.rules]; rules[idx] = { ...rule, value: e.target.value }; setTriggers({ ...triggers, rules }); }} />
                      </div>
                    )}
                    {rule.type === "channel" && (
                      <Select value={rule.value || ""} onValueChange={(v) => {
                        const rules = [...triggers.rules]; rules[idx] = { ...rule, value: v }; setTriggers({ ...triggers, rules });
                      }}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder={t("builder.chooseNumber")} /></SelectTrigger>
                        <SelectContent>
                          {sessions.map(s => (
                            <SelectItem key={s.sessionId} value={s.sessionId}>{s.name} {s.phone_number ? `(${s.phone_number})` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {rule.type === "new_contact" && (
                      <p className="text-[10px] text-muted-foreground">{t("builder.newContactNote")}</p>
                    )}
                  </div>
                ))}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] text-muted-foreground shrink-0">{t("builder.ifSeveral")}</Label>
                    <Select value={triggers.match} onValueChange={(v) => setTriggers({ ...triggers, match: v })}>
                      <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t("builder.any")}</SelectItem>
                        <SelectItem value="all">{t("builder.all")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] text-muted-foreground shrink-0">{t("builder.priority")}</Label>
                    <Input type="number" value={triggers.priority} className="h-7 text-xs flex-1"
                      onChange={(e) => setTriggers({ ...triggers, priority: parseInt(e.target.value) || 0 })} />
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 leading-snug">{t("builder.priorityNote")}</p>
                </div>
              </div>
            )}
            <Button size="sm" variant="outline" className="w-full h-7 text-xs"
              onClick={() => setTriggers({ ...triggers, rules: [...triggers.rules, { type: "tag", op: "has", value: "" }] })}>
              {t("builder.addRule")}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges.map(e => (e.selected ? { ...e, style: { ...e.style, stroke: "#FF453A", strokeWidth: 2 } } : e))}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            deleteKeyCode={["Backspace", "Delete"]}
            nodeTypes={nodeTypes}
            fitView
            className="workflow-canvas"
            defaultEdgeOptions={{ animated: true, style: { stroke: "#27272A" } }}
          >
            <Background color="#333" gap={20} size={1} />
            <Controls />
            <MiniMap
              nodeColor={() => "#00E676"}
              maskColor="rgba(0,0,0,0.8)"
              style={{ background: "#0A0A0A" }}
            />
          </ReactFlow>
        </div>

        {/* Config panel */}
        {selectedNode && selectedNode.type !== "start" && (
          <div className="w-72 border-l border-border bg-gray-50 p-3 overflow-y-auto shrink-0">
            {renderNodeConfig()}
          </div>
        )}
      </div>
    </div>
  );
}
